/* eslint-disable max-lines -- Pre-existing violation */
import {
  Action,
  Card,
  closeCurrentDialog,
  closeDialog,
  openConfirmDialog,
  openDialog,
  Separator,
  showNotification,
  TabProps,
  Tabs,
} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {always, head, isNil, mergeAll} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {
  GetWarehousesResponse,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
} from '@dms/api/warehouse';
import {
  GetMetadaServiceCaseApiResponse,
  PostMaterialBasketItemForPriceListFeApiArg,
  PostMaterialBasketItemForPriceListFeApiResponse,
  PostMaterialBasketItemForWarehouseApiArg,
  useGetItemsBasketActionsQuery,
  useGetServiceOrderQuery,
  usePutItemsBasketActionMutation,
  WarehouseAction,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  ArticleFolderTree,
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  ItemData,
  ItemInformation,
  logError,
  PriceListExternalFilters,
  PriceListRowData,
  refreshDmsDatagrid,
  WarehouseTabExternalFilters,
  usePermissions,
  useWarehouseDataGridTreeFolder,
  WarehouseAvailability,
  WarehouseAvailabilityQueryModifier,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {
  AddCreatedArticleAction,
  AddCreatedArticleDialog,
  ArticleCreate,
  getSingleOrUndefined,
  PriceListDetail,
} from '@dms/shared-aftersales';

import {
  buildArray,
  composePath,
  suffixTestId,
  TestIdProps,
  useLatestValue,
  useNavigate,
} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ADD_MATERIAL_GRID_CODE} from '../constants/ADD_MATERIAL_GRID_CODE';
import {ExternalSystemStateType} from '../types/externalSystemStateType';
import {OpenXentryLink} from './OpenXentryLink';
import {SupplierCatalogPicker} from './SupplierCatalogPicker/SupplierCatalogPicker';

const EXTERNAL_SYSTEM_ADD_KEY = 'xentry-xhpi-cart';
const EXTERNAL_SYSTEM_UPLOAD_KEY = 'xentry-xhpi-inquiry';
const DSMATERIAL_EXTERNAL_FILTER_ID = 'dsmaterial-external-filter';
const PRICE_LIST_EXTERNAL_FILTER_ID = 'price-list-external-filter';
const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'supplierAccount',
  'supplierOrderType',
  'branchId',
  'authorizationProfileId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

const SPACE_AROUND_DG_FROM_VH_IN_REM = 8.6;

const TABS_IDS = {
  warehouseTab: 'serviceOrder-warehouseTab',
  priceListTab: 'serviceOrder-priceListTab',
};

const CREATE_ARTICLE_DIALOG = 'createArticleDialog';
const ADD_CREATED_ARTICLE_DIALOG = 'addCreatedArticleDialog';
const WAREHOUSE_AVAILABILITY_DIALOG = 'warehouseAvailabilityDialog';

interface MaterialListCardProps extends TestIdProps {
  onExternalSystemChange: (state: ExternalSystemStateType) => void;
  onWarehouseArticleAdd: (arg: PostMaterialBasketItemForWarehouseApiArg) => Promise<boolean>;
  onPriceListItemAdd: (
    arg: PostMaterialBasketItemForPriceListFeApiArg
  ) => Promise<void | PostMaterialBasketItemForPriceListFeApiResponse>;
  shouldResetSmartSearch: boolean;
  availableWarehouses: GetWarehousesResponse;
  serviceCase: GetMetadaServiceCaseApiResponse;
  hasPendingRequest: boolean;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function MaterialListCard(props: MaterialListCardProps) {
  const {serviceCaseId, orderId, addMaterialRequestJobId} = useWorkshopUrl();

  const branchId = props.serviceCase?.branchId;
  const authorizationProfileId = props.serviceCase?.authorizationProfileId;

  const navigate = useNavigate();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [dataGridFilters, setDataGridFilters] = useState<Record<string, string>>({});

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder({
    availableWarehouses: props.availableWarehouses,
  });

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {branchId, authorizationProfileId}]);

  const {data} = useGetItemsBasketActionsQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addMaterialRequestJobId,
    basketType: 'material-basket',
  });
  const [putItemsBasketAction, {isLoading: isPutItemsLoading}] = usePutItemsBasketActionMutation();

  const {currentData: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId,
    serviceOrderId: orderId,
  });

  const [getArticleIdByManufacturerNumberAndManufacturerId] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const externalSystemAddOption = data?.itemsBasketActions?.options?.find(
    (option) => option?.key === EXTERNAL_SYSTEM_ADD_KEY
  );
  const externalSystemUploadOption = data?.itemsBasketActions?.options?.find(
    (option) => option?.key === EXTERNAL_SYSTEM_UPLOAD_KEY
  );

  const addMaterialQueryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([
        filter,
        {
          serviceOrderId: orderId,
          serviceCaseId,
          serviceJobId: addMaterialRequestJobId,
        },
      ]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [orderId, serviceCaseId, addMaterialRequestJobId, dataGridModifier]
  );

  const onAddClick = () => {
    putItemsBasketAction({
      basketType: 'material-basket',
      body: {actionKey: EXTERNAL_SYSTEM_ADD_KEY},
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
    })
      .unwrap()
      .then((response) => {
        openDialog(
          <OpenXentryLink
            xentryLink={response?.xentry_xhpi_cart?.xhpiUrl}
            onClose={closeCurrentDialog}
          />,
          {size: 'small'}
        );
        props.onExternalSystemChange('PREPARED');
      })
      .catch(handleApiError);
  };

  const onUploadClick = () => {
    openConfirmDialog({
      text: i18n.t('entity.addMaterial.lables.uploadFromXentry'),
      onConfirm: () =>
        putItemsBasketAction({
          basketType: 'material-basket',
          body: {actionKey: EXTERNAL_SYSTEM_UPLOAD_KEY},
          serviceCaseId,
          serviceOrderId: orderId,
          serviceJobId: addMaterialRequestJobId,
        })
          .unwrap()
          .then(() => props.onExternalSystemChange('UPLOAD_SCCESS'))
          .catch((error: Error) =>
            handleApiError(error, {callback: () => props.onExternalSystemChange('UPLOAD_ERROR')})
          ),
    });
  };

  const handleSendToBasket = async (articleId: string, actionKey?: string) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    if (actionKey) {
      const warehouseAction = match<string, WarehouseAction | null>(actionKey)
        .with('nonBinding', always('non-binding'))
        .with('dispatch', always('dispatch-request'))
        .with('request', always('request'))
        .otherwise(always(null));

      if (warehouseAction) {
        await props.onWarehouseArticleAdd({
          serviceCaseId,
          serviceOrderId: orderId,
          serviceJobId: addMaterialRequestJobId,
          warehouseAction,
          body: {warehouseArticleId: articleId},
        });
        closeCurrentDialog();
        return;
      }
    }

    await props.onPriceListItemAdd({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
      body: {warehouseArticleId: articleId},
    });

    closeCurrentDialog();
  };

  const handleOpenWarehouseAvailabilityDialog = (
    itemInformation: ItemInformation,
    queryModifier: WarehouseAvailabilityQueryModifier,
    supplierId?: string,
    supplierOrderTypeCode?: string
  ) => {
    const handleOnItemClick = async (item: ItemData) => {
      const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
        warehouseId: item.warehouseId,
        manufacturerNumber: item.catalogNumber,
        manufacturerId: item.manufacturerId,
        supplierId,
        supplierOrderTypeCode,
      })
        .unwrap()
        .catch(handleApiError);

      const articleId = articleIdResponse?.articleId;

      if (articleId) {
        handleSendToBasket(articleId, item.actionKey);
        return;
      }

      openDialog(
        <ArticleCreate
          supplierId={supplierId}
          supplierOrderTypeCode={supplierOrderTypeCode}
          defaultValues={{
            warehouseId: item.warehouseId,
            catalogNumber: item.catalogNumber,
            manufacturerId: item.manufacturerId,
            name: itemInformation.name,
          }}
          hasAddToBasketButton
          onAddToBasket={(articleId) => {
            handleSendToBasket(articleId, item.actionKey);
            closeCurrentDialog();
          }}
          onArticleCreate={(createdArticleData) => {
            handleSendToBasket(createdArticleData.articleId, item.actionKey);
            closeCurrentDialog();
          }}
          onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
          data-testid={testIds.workshop.serviceOrders('dialogs.createArticle')}
        />,
        {
          id: CREATE_ARTICLE_DIALOG,
          title: i18n.t('entity.warehouse.labels.createNewArticle'),
          withAdditionalFooter: true,
          scrollBehavior: 'outside',
        }
      );
    };

    openDialog(
      <WarehouseAvailability
        onItemClick={handleOnItemClick}
        itemInformation={itemInformation}
        queryModifier={queryModifier}
      />,
      {
        id: WAREHOUSE_AVAILABILITY_DIALOG,
        title: i18n.t('entity.warehouse.labels.warehouseAvailability'),
        scrollBehavior: 'outside',
        size: 'large',
      }
    );
  };

  const handleAddCreatedArticle = (
    warehouseArticleId: string,
    warehouseAction: AddCreatedArticleAction
  ): Promise<void> | void => {
    if (hasPendingRequestRef.current) {
      showNotification.warning(i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction'));
      return;
    }

    return props
      .onWarehouseArticleAdd({
        serviceCaseId,
        serviceOrderId: orderId,
        serviceJobId: addMaterialRequestJobId,
        warehouseAction,
        body: {warehouseArticleId},
      })
      .then((isAdded) => {
        // on failure the chooser stays open so the user can retry
        if (!isAdded) {
          return;
        }

        closeDialog(ADD_CREATED_ARTICLE_DIALOG);
        refreshDmsDatagrid(ADD_MATERIAL_GRID_CODE);
      });
  };

  const openAddCreatedArticleDialog = (warehouseArticleId: string) => {
    openDialog(
      <AddCreatedArticleDialog
        onConfirm={(warehouseAction) =>
          handleAddCreatedArticle(warehouseArticleId, warehouseAction)
        }
        onClose={() => closeDialog(ADD_CREATED_ARTICLE_DIALOG)}
        data-testid={testIds.workshop.serviceOrders('dialogs.addCreatedArticle')}
      />,
      {
        id: ADD_CREATED_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.addCreatedArticle'),
        size: 'small',
        withAdditionalFooter: true,
      }
    );
  };

  const onSupplierCatalogClick = () => {
    const variantId = serviceOrder?.serviceOrderVariantId;
    if (!branchId || !authorizationProfileId || !variantId) {
      return;
    }

    openDialog(
      <SupplierCatalogPicker
        key={`${serviceCaseId}:${branchId}:${authorizationProfileId}`}
        branchId={branchId}
        authorizationProfileId={authorizationProfileId}
        onSelect={(part) => {
          closeCurrentDialog();
          handleOpenWarehouseAvailabilityDialog(
            {
              catalogNumber: part.oemCode,
              name: part.name,
              manufacturerLabel: part.manufacturerLabel,
              supplierLabel: part.supplierLabel,
            },
            {
              variantId,
              originObjectType: 'service-order-case',
              manufacturerId: part.manufacturer,
              catalogNumber: part.oemCode,
            },
            part.supplier,
            part.supplierOrderTypeCode ?? undefined
          );
        }}
        data-testid={testIds.workshop.serviceOrders('dialogs.supplierCatalog')}
      />,
      {title: i18n.t('entity.warehouse.labels.priceList'), size: 'large'}
    );
  };

  const onCreateArticleClick = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber:
            'manufacturerNumber' in dataGridFilters
              ? dataGridFilters.manufacturerNumber
              : undefined,
          manufacturerId:
            'manufacturerId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.manufacturerId)
              : undefined,
          warehouseId:
            'warehouseId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.warehouseId)
              : undefined,
          name: 'name' in dataGridFilters ? dataGridFilters.name : undefined,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
              })
            );
          }
          closeCurrentDialog();
          refreshDmsDatagrid(ADD_MATERIAL_GRID_CODE);

          if (createdArticleData.submittedButton === 'CREATE_AND_ADD') {
            openAddCreatedArticleDialog(createdArticleData.articleId);
          }
        }}
        submitButtonTitle={i18n.t('general.actions.create')}
        onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
        hasCreateAndEditButton
        hasCreateAndAddButton
        data-testid={testIds.workshop.serviceOrders('dialogs.createArticle')}
      />,
      {
        id: CREATE_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        withAdditionalFooter: true,
        scrollBehavior: 'outside',
      }
    );
  };

  const warehouseArticleActionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const warehouseArticleId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(warehouseArticleId)) {
      return;
    }

    const warehouseAction = match<string, WarehouseAction | null>(actionKey)
      .with('nonBinding', always('non-binding'))
      .with('dispatch', always('dispatch-request'))
      .with('request', always('request'))
      .otherwise(always(null));

    if (isNil(warehouseAction)) {
      return;
    }

    // returned promise keeps the grid's row action locked until the request settles, and the
    // refresh re-reads availability, which the just-added item has already changed
    return props
      .onWarehouseArticleAdd({
        serviceCaseId,
        serviceOrderId: orderId,
        serviceJobId: addMaterialRequestJobId,
        warehouseAction,
        body: {warehouseArticleId},
      })
      .then(() => refreshData());
  };

  const supplierPriceListActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const priceListId = isArray(rowId) ? head(rowId) : rowId;
    const catalogNumber = getColumnData<string>('catalogNumber')?.value;
    const name = getColumnData<string>('name')?.value;
    const manufacturer = getColumnData<{label: string; id: string}>('manufacturerId')?.value;
    const manufacturerId = manufacturer?.id;
    const supplier = getColumnData<{label: string; id: string}>('supplierId')?.value;

    if (!isString(catalogNumber)) {
      return logError('Catalog number is not defined');
    }

    if (!isString(name)) {
      return logError('Name is not defined');
    }

    if (!isString(manufacturerId)) {
      return logError('Manufacturer id is not defined');
    }

    if (!isString(manufacturer?.label)) {
      return logError('Manufacturer label is not defined');
    }

    if (!isString(supplier?.label)) {
      return logError('Supplier label is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
      supplierAccountCode: getColumnData<string>('supplierAccount', {isOptional: true})?.value,
      supplierOrderTypeCode: getColumnData<string>('supplierOrderType', {isOptional: true})?.value,
      branchId: getColumnData<string>('branchId', {isOptional: true})?.value,
      authorizationProfileId: getColumnData<string>('authorizationProfileId', {isOptional: true})
        ?.value,
    } as PriceListRowData;

    const itemInformation: ItemInformation = {
      catalogNumber,
      name,
      manufacturerLabel: manufacturer.label,
      supplierLabel: supplier.label,
    };

    match(actionKey)
      .with('addToBasket', () => {
        const variantId = serviceOrder?.serviceOrderVariantId;
        if (isNil(variantId)) {
          return logError('Service order variant is not available');
        }
        handleOpenWarehouseAvailabilityDialog(
          itemInformation,
          {
            variantId,
            originObjectType: 'service-order-case',
            manufacturerId,
            catalogNumber,
          },
          supplier.id,
          priceListRowData.supplierOrderTypeCode ?? undefined
        );
      })
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const cardActions = buildArray<Action>()
    .when(externalSystemAddOption && externalSystemAddOption.style !== 'hidden', {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t(`entity.addMaterial.actions.addFromExternalSystem`),
      isDisabled: externalSystemAddOption?.style === 'disabled',
      isLoading: externalSystemAddOption?.style === 'enable' && isPutItemsLoading,
      onClick: onAddClick,
    })
    .when(externalSystemUploadOption && externalSystemUploadOption.style !== 'hidden', {
      type: 'button',
      variant: 'link',
      leftIcon: 'file/upload',
      title: i18n.t(`entity.addMaterial.actions.uploadFromExternalSystem`),
      isDisabled: externalSystemUploadOption?.style === 'disabled',
      isLoading: externalSystemUploadOption?.style === 'enable' && isPutItemsLoading,
      onClick: onUploadClick,
    })
    .add({
      type: 'button',
      variant: 'secondary',
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.warehouse.labels.createNewArticle'),
      isDisabled: !articleCreate,
      tooltip: !articleCreate
        ? i18n.t('module.warehouse.label.missingArticleCreatePermission')
        : undefined,
      onClick: onCreateArticleClick,
    });

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      actions: cardActions,
      content: (
        <Box height="100%" width="100%">
          <Card isFullHeight data-testid={suffixTestId('content', props)}>
            <HStack height="100%">
              <Box flex={1} maxHeight="100%" overflow="auto">
                <VStack height="100%">
                  <Box>
                    <div id={DSMATERIAL_EXTERNAL_FILTER_ID}></div>
                  </Box>
                  <Space vertical={8} />
                  <ArticleFolderTree
                    ref={folderTreeRef}
                    data-testid={testIds.warehouse.articleList('articleFolderTree')}
                  />
                </VStack>
              </Box>

              <Separator orientation="vertical" />

              <Box flex={3}>
                <DmsDataGrid
                  // DG must be re-rendered on folder change to update the query modifier
                  key={`${dataGridModifier?.key}_${props.shouldResetSmartSearch}`}
                  gridCode={ADD_MATERIAL_GRID_CODE}
                  autoFocusSmartSearch
                  externalFilterId={DSMATERIAL_EXTERNAL_FILTER_ID}
                  onFilterChanged={setDataGridFilters as (filters: unknown) => void}
                  filterComponent={(datagridProps) => (
                    <WarehouseTabExternalFilters
                      data-testid={suffixTestId('dg-external-filters', props)}
                      {...datagridProps}
                    />
                  )}
                  actionCallback={warehouseArticleActionCallback}
                  emptyState={{
                    headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
                  }}
                  queryModifier={addMaterialQueryModifier}
                  _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'CUSTOM'}}
                  data-testid={testIds.workshop.serviceCaseDetail('warehouse-articles')}
                />
              </Box>
            </HStack>
          </Card>
        </Box>
      ),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      actions: buildArray<Action>().whenFeatureEnabled(
        featureFlags.AFS_WORKSHOP_EXPERIMENTAL_SUPPLIER_CATALOG_SEARCH,
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('entity.warehouse.labels.priceList'),
          isDisabled: !branchId || !authorizationProfileId || !serviceOrder?.serviceOrderVariantId,
          onClick: onSupplierCatalogClick,
        }
      ),
      content: (
        <Box height={`calc(100vh - ${SPACE_AROUND_DG_FROM_VH_IN_REM}rem)`} width="100%">
          <Card isFullHeight data-testid={suffixTestId('content', props)}>
            <HStack height="100%">
              <Box flex={1}>
                <VStack height="100%">
                  <Box>
                    <div id={PRICE_LIST_EXTERNAL_FILTER_ID}></div>
                  </Box>
                </VStack>
              </Box>

              <Separator orientation="vertical" />

              <Box flex={3}>
                <DmsDataGrid
                  gridCode={WAREHOUSE_GRID_CODES.priceListBasket}
                  autoFocusSmartSearch
                  actionCallback={supplierPriceListActionCallback}
                  externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
                  filterComponent={PriceListExternalFilters}
                  predefinedColumns={PREDEFINED_COLUMNS}
                  emptyState={{
                    headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
                  }}
                  queryModifier={queryModifier}
                  data-testid={suffixTestId('workCatalogue', props)}
                />
              </Box>
            </HStack>
          </Card>
        </Box>
      ),
    },
  ];

  return (
    <VStack height="100%" grow={1}>
      <Tabs
        variant="fullHeight"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={(id) => setActiveTabId(id)}
        data-testid={testIds.workshop.serviceCaseDetail('tabs')}
        isLazy
        isFullHeight
        lazyBehavior="unmount"
      />
    </VStack>
  );
}
